import { FC, useState } from 'react'

import { Radio } from 'antd'
import { type Dayjs } from 'dayjs'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import InfoIcon from 'src/Assets/Svg/common/info.svg?react'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import { FullScreenModal } from 'src/Components/FullScreenModal'
import { formatDate } from 'src/Constants/formatDate'
import dayjs from 'src/Helpers/dayjs'
import { PrimaryRadio } from 'src/Components/PrimaryRadio'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { useUpdateSpotifyCampaignReleaseDateMutation } from 'src/Redux/spotify-campaignApi'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'

import { ReleaseDateField } from './ReleaseDateField'

type Confirmation = 'yes' | 'no'

export const isReleaseDateDisabled = (date: Dayjs): boolean =>
  !date.isAfter(dayjs(), 'day') ||
  !dayjs.utc(date.format(formatDate.formatDateISO)).isAfter(dayjs.utc(), 'day')

export const toReleaseDatePickerValue = (releaseDate: string): Dayjs =>
  dayjs(dayjs.utc(releaseDate).format(formatDate.formatDateISO))

const StyledModal = styled(FullScreenModal)`
  height: 100%;
`

const Container = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
  padding: 24px 16px 96px;
  flex-direction: column;
  gap: 24px;
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';

  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    width: 600px;
    margin: 0 auto;
    padding: 64px 0 96px;
  }
`

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 150%;
`

const ReleaseDateBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Question = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
`

const RadioGroup = styled(Radio.Group)`
  display: flex;
  width: 100%;
  gap: 8px;
`

const DateConfirmationOption = styled(PrimaryRadio)`
  flex: 1;
  min-width: 0;
  height: 56px;
  margin: 0;
  padding: 8px 16px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  font-size: 14px;

  &.ant-radio-wrapper {
    align-items: center;
  }

  &.ant-radio-wrapper > span:not(.ant-radio) {
    padding-inline: 12px 0;
  }
`

const Info = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding-left: 16px;
  font-size: 14px;
  line-height: 150%;
`

const InfoIconWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  padding-top: 4px;
`

const Buttons = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  display: flex;
  width: 100%;
  justify-content: center;
  gap: 24px;
  padding: 8px 16px;
  border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
`

const SaveButton = styled(CTAButton)`
  padding: 0 40px;
`

export const ReleaseDateModal: FC = () => {
  const { number: campaignId } = useParams<{ number: string }>()
  const { releaseDate } = useSpotifyCampaignStatus()
  const { setSearch } = useCustomSearchParams()
  const [confirmation, setConfirmation] = useState<Confirmation | null>(
    releaseDate ? 'yes' : null,
  )
  const [value, setValue] = useState(
    releaseDate ? toReleaseDatePickerValue(releaseDate) : null,
  )
  const [updateReleaseDate, { isLoading }] =
    useUpdateSpotifyCampaignReleaseDateMutation()

  const close = (): void => setSearch({ modal: null })
  const save = (): void => {
    if (!campaignId || !value) {
      return
    }
    updateReleaseDate({
      id: campaignId,
      releaseDate: value.format(formatDate.formatDateISO),
    })
      .unwrap()
      .then(close)
      .catch(() => null)
  }

  return (
    <StyledModal showCloseButton onCancel={close} prefixCls='releaseDate' open>
      <Container>
        <Title>Release date</Title>
        <ReleaseDateBlock>
          <Question>Is your release date confirmed?</Question>
          <RadioGroup
            value={confirmation}
            onChange={(event) =>
              setConfirmation(event.target.value as Confirmation)
            }
          >
            <DateConfirmationOption value='yes'>Yes</DateConfirmationOption>
            <DateConfirmationOption value='no'>No</DateConfirmationOption>
          </RadioGroup>
          {confirmation && (
            <ReleaseDateField
              label={
                confirmation === 'yes'
                  ? 'Release date'
                  : 'Estimated release date'
              }
              value={value}
              disabledDate={isReleaseDateDisabled}
              onChange={setValue}
            />
          )}
          {confirmation === 'no' && (
            <Info>
              <InfoIconWrapper>
                <InfoIcon />
              </InfoIconWrapper>
              Even if you’re unsure, please pick a tentative date for now.
            </Info>
          )}
        </ReleaseDateBlock>
        <Buttons>
          <PrimaryDottedButton onClick={close}>Go Back</PrimaryDottedButton>
          <SaveButton
            onClick={save}
            disabled={!confirmation || !value}
            loading={isLoading}
          >
            Save info
          </SaveButton>
        </Buttons>
      </Container>
    </StyledModal>
  )
}
